import Phaser from 'phaser';
import type {
  GameRuntime,
  GameRuntimeConfig,
  GameRuntimeEvents,
  GameRuntimeState,
} from '@adgf/game-engine';

/**
 * Memory Challenge — constants.
 * Round 1 shows 3 symbols; each cleared round adds one symbol and the
 * show time shrinks. A single mistake ends the run.
 */
const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;
const SYMBOL_COUNT = 6;
const START_LENGTH = 3;
const MAX_LENGTH = 8;
const POINTS_PER_CHOICE = 10;
const POINTS_PER_ROUND = 50;
const COLOR_BG = 0x1a1a2e;
const COLOR_IDLE = 0x30475e;
const COLOR_HIGHLIGHT = 0xf0a500;
const COLOR_CORRECT = 0x2ecc71;
const COLOR_WRONG = 0xe94560;
const SYMBOL_COLORS = [0xe94560, 0xf0a500, 0x2ecc71, 0x3498db, 0x9b59b6, 0xe67e22];

export type ChoiceOutcome = 'correct' | 'wrong' | 'roundClear';

/**
 * MemoryLogic — pure, Phaser-free rules engine for the game. It owns the
 * sequence generation, the current input position and the difficulty
 * ramp, so it can be unit-tested without a canvas.
 */
export class MemoryLogic {
  readonly symbolCount: number;
  sequence: number[] = [];
  inputIndex = 0;
  round = 1;
  sequenceLength: number;

  constructor(symbolCount: number = SYMBOL_COUNT, startLength: number = START_LENGTH) {
    this.symbolCount = symbolCount;
    this.sequenceLength = startLength;
  }

  /** Generate a fresh random sequence and reset the input pointer. */
  startSequence(rng: () => number = Math.random): number[] {
    this.sequence = [];
    for (let i = 0; i < this.sequenceLength; i++) {
      this.sequence.push(Math.floor(rng() * this.symbolCount));
    }
    this.inputIndex = 0;
    return this.sequence;
  }

  /** Submit a player's chosen symbol index. */
  choose(symbol: number): ChoiceOutcome {
    if (symbol === this.sequence[this.inputIndex]) {
      this.inputIndex++;
      if (this.inputIndex >= this.sequence.length) {
        this.round++;
        this.sequenceLength = Math.min(MAX_LENGTH, this.sequenceLength + 1);
        return 'roundClear';
      }
      return 'correct';
    }
    return 'wrong';
  }

  /** Total time (ms) the pattern is shown for the current round. */
  showTimeMs(): number {
    return Math.max(900, 1800 - (this.round - 1) * 180);
  }
}

/**
 * Callbacks the inner scene uses to talk back to the runtime wrapper.
 */
interface SceneCallbacks {
  addPoints(points: number): void;
  gameOver(): void;
  retry(): void;
  wantsStart(): boolean;
  onEvent(type: string, data?: Record<string, unknown>): void;
}

type Phase = 'idle' | 'show' | 'input' | 'feedback';

/**
 * MemoryScene — Phaser gameplay. It renders the symbol grid, plays the
 * SHOW -> HIDE -> INPUT -> FEEDBACK phase loop and delegates scoring /
 * analytics to the MemoryGame wrapper.
 */
class MemoryScene extends Phaser.Scene {
  private readonly cb: SceneCallbacks;
  private readonly logic = new MemoryLogic();

  private roundText: Phaser.GameObjects.Text | null = null;
  private scoreText: Phaser.GameObjects.Text | null = null;
  private statusText: Phaser.GameObjects.Text | null = null;
  private buttons: Phaser.GameObjects.Arc[] = [];
  private readyOverlay: Phaser.GameObjects.Container | null = null;
  private overOverlay: Phaser.GameObjects.Container | null = null;

  private timers: Phaser.Time.TimerEvent[] = [];
  private phase: Phase = 'idle';
  private running = false;
  private created = false;

  constructor(cb: SceneCallbacks) {
    super({ key: 'MemoryScene' });
    this.cb = cb;
  }

  create(): void {
    const { width, height } = this.scale;

    this.roundText = this.add
      .text(70, 36, 'Round 1', { fontSize: '24px', color: '#f0a500', fontStyle: 'bold' })
      .setOrigin(0.5);
    this.scoreText = this.add
      .text(width - 70, 36, 'Score: 0', { fontSize: '24px', color: '#ffffff' })
      .setOrigin(0.5);
    this.statusText = this.add
      .text(width / 2, 90, '', { fontSize: '22px', color: '#a0a0a0' })
      .setOrigin(0.5);

    this.buildButtons(width, height);
    this.buildReadyOverlay(width, height);
    this.buildGameOverOverlay(width, height);

    this.created = true;
    if (this.cb.wantsStart()) this.beginRound();
  }

  /** Called by the wrapper when start() fires (possibly before create()). */
  requestBegin(): void {
    if (this.created) this.beginRound();
  }

  /** Begin a fresh round: build the sequence and play the SHOW phase. */
  beginRound(): void {
    this.running = true;
    this.clearTimers();
    this.buttons.forEach((b) => b.setFillStyle(COLOR_IDLE));
    this.hideOverlay(this.readyOverlay);
    this.hideOverlay(this.overOverlay);

    const sequence = this.logic.startSequence();
    this.roundText?.setText(`Round ${this.logic.round}`);
    this.setStatus('Watch...');
    this.phase = 'show';
    this.playSequence(sequence);
  }

  /** Clear pending timers / reset display (used by retry). */
  resetRound(): void {
    this.clearTimers();
    this.logic.round = 1;
    this.logic.sequenceLength = START_LENGTH;
    this.buttons.forEach((b) => b.setFillStyle(COLOR_IDLE));
    this.roundText?.setText('Round 1');
    this.scoreText?.setText('Score: 0');
    this.setStatus('');
    this.phase = 'idle';
    this.running = false;
  }

  setScoreDisplay(score: number): void {
    this.scoreText?.setText(`Score: ${score}`);
  }

  showFinalScore(score: number): void {
    const label = this.overOverlay?.getByName('finalScoreText') as Phaser.GameObjects.Text | null;
    label?.setText(`Final Score: ${score}`);
  }

  // ---- internals -------------------------------------------------------

  private buildButtons(width: number, height: number): void {
    const cols = 3;
    const radius = 56;
    const gapX = 200;
    const gapY = 180;
    const startX = width / 2 - gapX;
    const startY = height / 2 - gapY + 30;
    for (let i = 0; i < SYMBOL_COUNT; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * gapX;
      const y = startY + row * gapY;
      const btn = this.add.circle(x, y, radius, COLOR_IDLE);
      btn.setStrokeStyle(3, SYMBOL_COLORS[i]);
      btn.setInteractive({ useHandCursor: true });
      btn.on('pointerdown', () => this.onSymbolPressed(i));
      this.buttons.push(btn);
    }
  }

  private playSequence(sequence: number[]): void {
    const showMs = this.logic.showTimeMs();
    const flashMs = Math.min(600, Math.floor(showMs / Math.max(1, sequence.length)));
    sequence.forEach((symbol, i) => {
      this.schedule(i * flashMs, () => this.flash(symbol, 260));
    });
    this.schedule(sequence.length * flashMs, () => {
      this.buttons.forEach((b) => b.setFillStyle(COLOR_IDLE));
      this.phase = 'input';
      this.setStatus('Repeat the pattern!');
    });
  }

  private flash(index: number, holdMs: number): void {
    const btn = this.buttons[index];
    if (!btn) return;
    btn.setFillStyle(COLOR_HIGHLIGHT);
    this.schedule(holdMs, () => {
      if (this.phase === 'show') btn.setFillStyle(COLOR_IDLE);
    });
  }

  private onSymbolPressed(index: number): void {
    if (!this.running || this.phase !== 'input') return;
    const outcome = this.logic.choose(index);

    if (outcome === 'wrong') {
      this.phase = 'feedback';
      this.flashInstant(index, COLOR_WRONG);
      this.cb.onEvent('wrong_choice', { index });
      this.schedule(900, () => this.endGame());
      return;
    }

    this.flashInstant(index, COLOR_CORRECT);
    this.cb.addPoints(POINTS_PER_CHOICE);
    this.cb.onEvent('correct_choice', { index });

    if (outcome === 'roundClear') {
      this.cb.addPoints(POINTS_PER_ROUND);
      this.cb.onEvent('round_clear', { length: this.logic.sequenceLength - 1 });
      this.phase = 'feedback';
      this.schedule(900, () => this.beginRound());
    }
  }

  private flashInstant(index: number, color: number): void {
    const btn = this.buttons[index];
    if (btn) btn.setFillStyle(color);
  }

  private endGame(): void {
    this.running = false;
    this.phase = 'idle';
    this.showOverlay(this.overOverlay);
    this.cb.gameOver();
  }

  private setStatus(text: string): void {
    this.statusText?.setText(text);
  }

  private schedule(delay: number, fn: () => void): void {
    const t = this.time.delayedCall(delay, fn);
    this.timers.push(t);
  }

  private clearTimers(): void {
    this.timers.forEach((t) => t.remove(false));
    this.timers = [];
  }

  private buildReadyOverlay(width: number, height: number): void {
    this.readyOverlay = this.add.container(width / 2, height / 2);
    const title = this.add.text(0, -40, 'Memory Challenge', {
      fontSize: '36px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);
    const hint = this.add.text(0, 20, 'Watch the flashes, then repeat them in order', {
      fontSize: '18px',
      color: '#a0a0a0',
    });
    hint.setOrigin(0.5);
    const btn = this.add.rectangle(0, 90, 220, 64, COLOR_HIGHLIGHT);
    const btnText = this.add.text(0, 90, 'START', {
      fontSize: '24px',
      color: '#1a1a2e',
      fontStyle: 'bold',
    });
    btnText.setOrigin(0.5);
    btn.setInteractive({ useHandCursor: true });
    btn.on('pointerdown', () => this.cb.wantsStart() || this.requestBegin());
    this.readyOverlay.add([title, hint, btn, btnText]);
  }

  private buildGameOverOverlay(width: number, height: number): void {
    this.overOverlay = this.add.container(width / 2, height / 2);
    const title = this.add.text(0, -60, 'Game Over', {
      fontSize: '40px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);
    const score = this.add.text(0, -10, 'Final Score: 0', {
      fontSize: '24px',
      color: '#e94560',
    });
    score.setOrigin(0.5);
    score.setName('finalScoreText');
    const btn = this.add.rectangle(0, 70, 240, 64, COLOR_HIGHLIGHT);
    const btnText = this.add.text(0, 70, 'Try Again', {
      fontSize: '24px',
      color: '#1a1a2e',
      fontStyle: 'bold',
    });
    btnText.setOrigin(0.5);
    btn.setInteractive({ useHandCursor: true });
    btn.on('pointerdown', () => this.cb.retry());
    this.overOverlay.add([title, score, btn, btnText]);
    this.overOverlay.setVisible(false);
  }

  private showOverlay(overlay: Phaser.GameObjects.Container | null): void {
    overlay?.setVisible(true);
    overlay?.setActive(true);
  }

  private hideOverlay(overlay: Phaser.GameObjects.Container | null): void {
    overlay?.setVisible(false);
    overlay?.setActive(false);
  }
}

/**
 * MemoryGame — GameRuntime wrapper. Owns the Phaser.Game instance, the
 * score and the runtime state machine. The inner scene delegates gameplay
 * events back through SceneCallbacks.
 */
export class MemoryGame implements GameRuntime {
  private readonly config: GameRuntimeConfig;
  private readonly events: GameRuntimeEvents;
  private state: GameRuntimeState = 'idle';
  private score = 0;
  private wantStart = false;
  private game: Phaser.Game | null = null;
  private scene: MemoryScene | null = null;

  constructor(config: GameRuntimeConfig, events: GameRuntimeEvents = {}) {
    this.config = config;
    this.events = events;
  }

  mount(): void {
    if (this.state !== 'idle') return;

    this.scene = new MemoryScene(this.buildCallbacks());

    this.game = new Phaser.Game({
      type: Phaser.AUTO,
      parent: this.config.parent,
      width: this.config.width ?? GAME_WIDTH,
      height: this.config.height ?? GAME_HEIGHT,
      backgroundColor: COLOR_BG,
      scale: {
        mode: Phaser.Scale.FIT,
        width: this.config.width ?? GAME_WIDTH,
        height: this.config.height ?? GAME_HEIGHT,
      },
      scene: [this.scene],
    });

    this.state = 'mounted';
  }

  start(): void {
    this.wantStart = true;
    if (
      this.state === 'mounted' ||
      this.state === 'paused' ||
      this.state === 'gameover'
    ) {
      this.state = 'running';
      this.events.onStart?.();
      this.scene?.requestBegin();
    }
  }

  pause(): void {
    if (this.state !== 'running') return;
    this.state = 'paused';
    this.scene?.scene?.pause();
    this.events.onPause?.();
  }

  resume(): void {
    if (this.state !== 'paused') return;
    this.state = 'running';
    this.scene?.scene?.resume();
    this.events.onResume?.();
  }

  destroy(): void {
    if (this.state === 'destroyed') return;
    this.game?.destroy(true);
    this.game = null;
    this.scene = null;
    this.state = 'destroyed';
  }

  getScore(): number {
    return this.score;
  }

  getState(): GameRuntimeState {
    return this.state;
  }

  retry(): void {
    this.score = 0;
    this.events.onScoreChange?.(this.score);
    this.scene?.resetRound();
    this.scene?.setScoreDisplay(this.score);
    this.state = 'running';
    this.events.onRetry?.();
    this.wantStart = true;
    this.scene?.requestBegin();
  }

  private buildCallbacks(): SceneCallbacks {
    return {
      addPoints: (points: number) => {
        this.score += points;
        this.events.onScoreChange?.(this.score);
        this.scene?.setScoreDisplay(this.score);
      },
      gameOver: () => {
        this.state = 'gameover';
        this.scene?.showFinalScore(this.score);
        this.events.onGameOver?.(this.score, {});
      },
      retry: () => this.retry(),
      wantsStart: () => this.wantStart,
      onEvent: (type: string, data?: Record<string, unknown>) => {
        this.events.onEvent?.(type, data);
      },
    };
  }
}
